/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCard, Download, Eye,
  CheckCircle, XCircle, Clock, RefreshCw,
  DollarSign, TrendingUp, Calendar,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { usePayments } from '@/hooks/api/usePayments';
import { Payment, PaymentStatus } from '@/types/superadmin.types';
import { PaymentReceiptModal } from '@/components/superadmin/PaymentReceiptModal';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import { SearchInput } from '@/components/ui';

// Status Badge
const StatusBadge: React.FC<{ status: PaymentStatus; t: (key: string) => string }> = ({ status, t }) => {
  const config: Record<PaymentStatus, { bg: string; text: string; icon: typeof CheckCircle; labelKey: string }> = {
    PAID: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle, labelKey: 'payments.status.paid' },
    PENDING: { bg: 'bg-amber-500/15', text: 'text-amber-500', icon: Clock, labelKey: 'payments.status.pending' },
    PROCESSING: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: Clock, labelKey: 'payments.status.processing' },
    FAILED: { bg: 'bg-red-500/15', text: 'text-red-500', icon: XCircle, labelKey: 'payments.status.failed' },
    REFUNDED: { bg: 'bg-purple-500/15', text: 'text-purple-500', icon: RefreshCw, labelKey: 'payments.status.refunded' },
    PARTIALLY_REFUNDED: { bg: 'bg-purple-500/15', text: 'text-purple-500', icon: RefreshCw, labelKey: 'payments.status.partiallyRefunded' },
    CANCELLED: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle, labelKey: 'payments.status.cancelled' },
  };
  const { bg, text, icon: Icon, labelKey } = config[status] || config.PENDING;

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(labelKey)}
    </span>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="payments-table-row grid grid-cols-[1.5fr_100px_100px_1fr_1fr_120px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle animate-pulse">
    <div>
      <div className="h-4 w-32 bg-muted/20 rounded mb-2"></div>
      <div className="h-3 w-24 bg-muted/20 rounded"></div>
    </div>
    <div className="h-5 w-16 bg-muted/20 rounded col-amount"></div>
    <div className="h-5 w-16 bg-muted/20 rounded-full"></div>
    <div className="h-4 w-24 bg-muted/20 rounded col-method"></div>
    <div className="h-4 w-28 bg-muted/20 rounded col-reference"></div>
    <div className="h-4 w-20 bg-muted/20 rounded col-date"></div>
    <div className="w-7 h-7 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="w-10 h-10 rounded-lg bg-muted/20"></div>
    </div>
    <div className="h-6 w-20 bg-muted/20 rounded mb-1"></div>
    <div className="h-3 w-28 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  trend?: string;
  color: 'green' | 'blue' | 'red' | 'amber';
}> = ({ icon, value, label, trend, color }) => {
  const colors = {
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    red: 'bg-red-500/15 text-red-500',
    amber: 'bg-amber-500/15 text-amber-500',
  };

  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
        {trend && (
          <span className="text-xs font-semibold text-emerald-500 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> {trend}
          </span>
        )}
      </div>
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
};

// Format currency based on payment currency code
const formatPaymentAmount = (amount: number | undefined, currency: string = 'USD') => {
  if (amount === undefined || amount === null) return '-';
  if (currency === 'USD') return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  if (currency === 'GNF') return `${amount.toLocaleString()} FG`;
  if (currency === 'XOF' || currency === 'XAF') return `${amount.toLocaleString()} CFA`;
  return `${amount.toLocaleString()} ${currency}`;
};

// Main Payments Page
export default function PaymentsPage() {
  const { t } = useTranslation('platform');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  // Sort state
  const { sortConfig, handleSort, getSortParam } = useSort('paidAt', 'desc');

  // Use the payments hook with filters
  const { data, isLoading, isFetching, refetch } = usePayments({
    page: currentPage,
    size: 20,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    sort: getSortParam(),
  });

  const payments = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;
  const totalPages = data?.page?.totalPages || 1;
  const pageSize = 20;

  // Handle view payment receipt
  const handleViewReceipt = (payment: Payment) => {
    setSelectedPayment(payment);
    setIsReceiptModalOpen(true);
  };

  const handleCloseReceipt = () => {
    setIsReceiptModalOpen(false);
    setSelectedPayment(null);
  };

  // Calculate stats from current data
  const stats = useMemo(() => {
    const totalPaid = payments.filter(p => p.status === 'PAID').reduce((sum, p) => sum + p.amount, 0);
    const totalPending = payments.filter(p => p.status === 'PENDING').reduce((sum, p) => sum + p.amount, 0);
    const totalFailed = payments.filter(p => p.status === 'FAILED').length;
    return { totalPaid, totalPending, totalFailed, total: payments.length };
  }, [payments]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <CreditCard className="w-6 h-6" /> {t('payments.title')}
          </h1>
          <p className="text-sm text-muted">{t('payments.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-border-subtle rounded-lg text-sm font-medium text-foreground hover:bg-hover">
            <Download className="w-4 h-4" /> {t('common.export')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<DollarSign className="w-5 h-5" />}
              value={`$${stats.totalPaid.toLocaleString()}`}
              label={t('payments.stats.collected')}
              trend="+12%"
              color="green"
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              value={`$${stats.totalPending.toLocaleString()}`}
              label={t('payments.stats.pending')}
              color="amber"
            />
            <StatCard
              icon={<XCircle className="w-5 h-5" />}
              value={stats.totalFailed.toString()}
              label={t('payments.stats.failed')}
              color="red"
            />
            <StatCard
              icon={<Calendar className="w-5 h-5" />}
              value={totalElements.toString()}
              label={t('payments.stats.total')}
              color="blue"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('payments.searchPlaceholder')}
          className="flex-1 max-w-xs"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | 'ALL')}
          className="px-3 py-2 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
        >
          <option value="ALL">{t('payments.filters.allStatus')}</option>
          <option value="PAID">{t('payments.status.paid')}</option>
          <option value="PENDING">{t('payments.status.pending')}</option>
          <option value="FAILED">{t('payments.status.failed')}</option>
          <option value="REFUNDED">{t('payments.status.refunded')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <div className="payments-table-header grid grid-cols-[1.5fr_100px_100px_1fr_1fr_120px_80px] gap-4 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">
            <SortableHeader
              columnKey="payerName"
              label={t('payments.table.organization')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-amount">
            <SortableHeader
              columnKey="amount"
              label={t('payments.table.amount')}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="right"
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">
            <SortableHeader
              columnKey="status"
              label={t('payments.table.status')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-method">
            <SortableHeader
              columnKey="paymentMethod"
              label={t('payments.table.method')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-reference">
            <SortableHeader
              columnKey="externalReference"
              label={t('payments.table.reference')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-date">
            <SortableHeader
              columnKey="paidAt"
              label={t('payments.table.date')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('payments.table.actions')}</div>
        </div>

        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <CreditCard className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('payments.noPayments')}</p>
          </div>
        ) : (
          payments.map((payment) => (
            <div
              key={payment.id}
              className="payments-table-row grid grid-cols-[1.5fr_100px_100px_1fr_1fr_120px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover"
            >
              <div>
                <div className="font-medium text-foreground text-base">{payment.organizationName}</div>
                <div className="text-sm text-muted">{payment.invoiceNumber}</div>
              </div>
              <div className="font-mono text-base font-semibold text-foreground col-amount">
                {formatPaymentAmount(payment.amount, payment.currency)}
              </div>
              <div><StatusBadge status={payment.status} t={t} /></div>
              <div className="text-base text-secondary col-method">{payment.paymentMethod}</div>
              <div className="font-mono text-sm text-muted col-reference">{payment.reference}</div>
              <div className="text-sm text-muted col-date">{formatDate(payment.paidAt || payment.createdAt)}</div>
              <div>
                <button
                  onClick={() => handleViewReceipt(payment)}
                  className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
                  title={t('payments.viewReceipt')}
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!isLoading && totalElements > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted">
            {t('payments.pagination.showing', {
              from: currentPage * pageSize + 1,
              to: Math.min((currentPage + 1) * pageSize, totalElements),
              total: totalElements
            })}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">
              {t('payments.pagination.page', { current: currentPage + 1, total: Math.max(1, totalPages) })}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Receipt Modal */}
      <PaymentReceiptModal
        payment={selectedPayment}
        isOpen={isReceiptModalOpen}
        onClose={handleCloseReceipt}
      />

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .payments-table-header,
          .payments-table-row {
            grid-template-columns: 1fr 80px 80px 60px !important;
          }
          .col-method,
          .col-reference,
          .col-date {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .payments-table-header,
          .payments-table-row {
            grid-template-columns: 1fr 60px 60px !important;
          }
          .col-amount {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
